import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  DatePicker,
  Badge,
  Descriptions,
  List,
  Avatar
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  CrownOutlined,
  PlusOutlined,
  ReloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { vipService, VIPParking, VIPVehicle } from '../../../services/vipService';
import VIPForm from './VIPForm';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface VIPListProps {
  refreshKey: number;
  onDataChange: () => void;
}

const VIPList: React.FC<VIPListProps> = ({ refreshKey, onDataChange }) => {
  const [loading, setLoading] = useState(false);
  const [vipList, setVipList] = useState<VIPParking[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    vipLevel: '',
    startDate: '',
    endDate: '',
  });
  const [selectedVIP, setSelectedVIP] = useState<VIPParking | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  useEffect(() => {
    loadVIPList();
  }, [refreshKey, pagination.current, pagination.pageSize, filters]);

  const loadVIPList = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search || undefined,
        status: filters.status || undefined,
        vipLevel: filters.vipLevel || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      };

      const response = await vipService.getVIPParkings(params);
      const data = Array.isArray(response?.data) ? response.data : [];
      const total =
        (response?.pagination && (response.pagination.totalRecords ?? response.pagination.total)) || 0;

      setVipList(data);
      setPagination(prev => ({
        ...prev,
        total,
      }));
    } catch (error) {
      console.error('載入VIP列表失敗:', error);
      message.error('載入VIP列表失敗');
      // 防禦：在錯誤時重置列表與總數避免殘留狀態
      setVipList([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        startDate: '',
        endDate: '',
      }));
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDelete = async (id: string) => {
    try {
      await vipService.deleteVIPParking(id);
      message.success('VIP車位刪除成功');
      onDataChange();
      loadVIPList();
    } catch (error) {
      console.error('刪除VIP車位失敗:', error);
      message.error('刪除VIP車位失敗');
    }
  };

  const handleTableChange = (paginationInfo: any) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const showDetail = (vip: VIPParking) => {
    setSelectedVIP(vip);
    setDetailModalVisible(true);
  };

  const showEdit = (vip: VIPParking) => {
    setSelectedVIP(vip);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedVIP(null);
    onDataChange();
    loadVIPList();
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'green', text: '啟用中' },
      expired: { color: 'red', text: '已過期' },
      suspended: { color: 'orange', text: '已暫停' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getVIPLevelTag = (level: string) => {
    const levelConfig = {
      platinum: { color: 'purple', text: '白金會員' },
      gold: { color: 'gold', text: '黃金會員' },
      silver: { color: 'default', text: '銀牌會員' },
    };
    const config = levelConfig[level as keyof typeof levelConfig] || { color: 'default', text: level };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const isExpiringSoon = (validTo: string) => {
    const end = dayjs(validTo);
    const now = dayjs();
    const daysLeft = end.diff(now, 'day');
    return daysLeft <= 7 && daysLeft >= 0;
  };

  const columns: ColumnsType<VIPParking> = [
    {
      title: '車位編號',
      dataIndex: ['parkingSpot', 'spotNumber'],
      key: 'spotNumber',
      width: 100,
      render: (spotNumber: string) => (
        <Badge.Ribbon text="VIP" color="gold">
          <Tag color="blue">{spotNumber}</Tag>
        </Badge.Ribbon>
      ),
    },
    {
      title: '會員信息',
      key: 'owner',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            <UserOutlined style={{ marginRight: 4 }} />
            {record.owner.name}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <PhoneOutlined style={{ marginRight: 4 }} />
            {record.owner.phone}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.owner.email}
          </div>
        </div>
      ),
    },
    {
      title: 'VIP等級',
      dataIndex: 'vipLevel',
      key: 'vipLevel',
      width: 100,
      render: (level: string) => getVIPLevelTag(level),
    },
    {
      title: '車輛數量',
      key: 'vehicleCount',
      width: 80,
      render: (_, record) => (
        <Badge count={record.vehicles.length} showZero>
          <CarOutlined style={{ fontSize: '16px' }} />
        </Badge>
      ),
    },
    {
      title: '有效期',
      key: 'validity',
      width: 150,
      render: (_, record) => {
        const isExpiring = isExpiringSoon(record.validTo);
        return (
          <div>
            <div style={{ marginBottom: 2 }}>
              開始：{dayjs(record.validFrom).format('YYYY-MM-DD')}
            </div>
            <div style={{ color: isExpiring ? '#ff4d4f' : '#666' }}>
              結束：{dayjs(record.validTo).format('YYYY-MM-DD')}
              {isExpiring && <Tag color="red" style={{ marginLeft: 4, fontSize: '12px' }}>即將到期</Tag>}
            </div>
          </div>
        );
      },
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看詳情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showDetail(record)}
            />
          </Tooltip>
          <Tooltip title="編輯">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="確定要刪除這個VIP車位嗎？"
            description="刪除後車位將恢復為普通車位"
            onConfirm={() => handleDelete(record._id)}
            okText="確定"
            cancelText="取消"
          >
            <Tooltip title="刪除">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="vip-list">
      {/* 搜索和篩選區域 */}
      <Card className="filter-card" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="搜索會員姓名、電話或車牌"
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="狀態"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="active">啟用中</Option>
              <Option value="expired">已過期</Option>
              <Option value="suspended">已暫停</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="VIP等級"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('vipLevel', value)}
            >
              <Option value="platinum">白金會員</Option>
              <Option value="gold">黃金會員</Option>
              <Option value="silver">銀牌會員</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <RangePicker
              placeholder={['開始日期', '結束日期']}
              style={{ width: '100%' }}
              onChange={handleDateRangeChange}
            />
          </Col>
          <Col xs={24} sm={12} md={24} lg={6}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setFilters({
                    search: '',
                    status: '',
                    vipLevel: '',
                    startDate: '',
                    endDate: '',
                  });
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
              >
                重置
              </Button>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={loadVIPList}
              >
                篩選
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* VIP列表表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={vipList}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* VIP詳情模態框 */}
      <Modal
        title={
          <span>
            <CrownOutlined style={{ marginRight: 8, color: '#d4af37' }} />
            VIP車位詳情
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedVIP && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="車位編號" span={1}>
                <Badge.Ribbon text="VIP" color="gold">
                  <Tag color="blue">{selectedVIP.parkingSpot.spotNumber}</Tag>
                </Badge.Ribbon>
              </Descriptions.Item>
              <Descriptions.Item label="VIP等級" span={1}>
                {getVIPLevelTag(selectedVIP.vipLevel)}
              </Descriptions.Item>
              <Descriptions.Item label="會員姓名" span={1}>
                {selectedVIP.owner.name}
              </Descriptions.Item>
              <Descriptions.Item label="聯繫電話" span={1}>
                {selectedVIP.owner.phone}
              </Descriptions.Item>
              <Descriptions.Item label="電子郵箱" span={2}>
                {selectedVIP.owner.email}
              </Descriptions.Item>
              <Descriptions.Item label="有效期" span={2}>
                {dayjs(selectedVIP.validFrom).format('YYYY-MM-DD')} 至{' '}
            {dayjs(selectedVIP.validTo).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="狀態" span={1}>
                {getStatusTag(selectedVIP.status)}
              </Descriptions.Item>
              <Descriptions.Item label="創建時間" span={1}>
                {dayjs(selectedVIP.createdAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            <Card title="關聯車輛" size="small">
              <List
                dataSource={selectedVIP.vehicles}
                renderItem={(vehicle: VIPVehicle) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<CarOutlined />} />}
                      title={
                        <Space>
                          <span>{vehicle.licensePlate}</span>
                          <Tag color={vehicle.isActive ? 'green' : 'red'}>
                            {vehicle.isActive ? '啟用' : '停用'}
                          </Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <div>品牌型號：{vehicle.brand} {vehicle.model}</div>
                          <div>顏色：{vehicle.color}</div>
                          <div>添加時間：{dayjs(vehicle.addedAt).format('YYYY-MM-DD HH:mm')}</div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )}
      </Modal>

      {/* 編輯VIP模態框 */}
      <Modal
        title="編輯VIP車位"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedVIP && (
          <VIPForm
            initialData={selectedVIP}
            onSuccess={handleEditSuccess}
            onCancel={() => setEditModalVisible(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default VIPList;